import React, { useState } from 'react';
import { ThumbnailContainer, ThumbnailItem, ThumbnailImage, ThumbnailMenuWrapper, ThumbnailMenuButton } from './styles';
import { ImageType, useUpdatePointOfInterestImageTypeMutation } from '@/__generated__/graphql';
import { ThumbnailLoadingContainer } from '../styles';
import { CircularProgress, Menu, MenuItem } from '@mui/material';
import { ImageTypeBadge } from './ImageTypeBadge';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { ImageWithId } from '@/types';
import { useSetAtom } from 'jotai';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';

type ThumbnailListProps = {
  pointOfInterestId: string;
  imagesWithIds: ImageWithId[];
  currentIndex: number;
  handleThumbnailClick: (index: number) => void;
}

export const ThumbnailList = ({
  pointOfInterestId,
  imagesWithIds,
  currentIndex,
  handleThumbnailClick,
}: ThumbnailListProps) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [menuIndex, setMenuIndex] = useState<number | null>(null);
  const [updatePointOfInterestImageType] = useUpdatePointOfInterestImageTypeMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const setAllPOI = useSetAtom(poiState.structurePois);


  const menuAnchorOrigin = { vertical: 'bottom', horizontal: 'right' } as const;
  const menuTransformOrigin = { vertical: 'top', horizontal: 'right' } as const;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, index: number) => {
    event.stopPropagation();
    setAnchorElement(event.currentTarget);
    setMenuIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorElement(null);
    setMenuIndex(null);
  };

  const handleMarkAsPrimary = async (image: ImageWithId) => {
    await updatePointOfInterestImageType({
      variables: {
        input: {
          pointOfInterestId,
          imageId: image.id,
          imageType: ImageType.Primary,
        },
      },
      onCompleted: () => {
        handleMenuClose();
        setAllPOI((prevPois) =>
          prevPois.map((poi) => {
            if (poi.id !== pointOfInterestId) return poi;
            return {
              ...poi,
              files: poi.files?.map((file) => ({
                ...file,
                imageType:
                  file.id === image.id
                    ? ImageType.Primary
                    : file.imageType === ImageType.Primary
                      ? ImageType.Other
                      : file.imageType,
              })),
            };
          })
        );
        showSnackBar('Mark primary image successfully', 'success');
      },
      onError: (error) => {
        showSnackBar(error.message, 'error');
      },
    });
  };

  return (
    <ThumbnailContainer>
      {imagesWithIds.map((img, index) => (
        <ThumbnailItem
          key={img.id}
          isSelected={index === currentIndex}
          onClick={() => handleThumbnailClick(index)}
          sx={{
            cursor: img.isLoading ? 'default' : 'pointer',
            opacity: img.isLoading ? 0.7 : 1,
            position: 'relative',
          }}
        >
          {img.isLoading ? (
            <ThumbnailLoadingContainer>
              <CircularProgress size={24} />
            </ThumbnailLoadingContainer>
          ) : img.src ? (
            <ThumbnailImage src={img.src} alt={`Thumbnail ${img.name}`} />
          ) : (
            <ThumbnailLoadingContainer>
              No thumbnail images available
            </ThumbnailLoadingContainer>
          )}

          {!img.isLoading && <ImageTypeBadge type={img.imageType} />}
          {!img.isLoading && (img.imageType !== 'viewpoint' && img.imageType !== ImageType.Primary) && (
            <ThumbnailMenuWrapper>
              <ThumbnailMenuButton
                onClick={(e) => handleMenuOpen(e, index)}
              >
                <MoreVertIcon sx={{ fontSize: 18, fontWeight: 700 }} />
              </ThumbnailMenuButton>
              <Menu
                elevation={0}
                anchorEl={anchorElement}
                open={menuIndex === index && Boolean(anchorElement)}
                onClose={handleMenuClose}
                anchorOrigin={menuAnchorOrigin}
                transformOrigin={menuTransformOrigin}
              >
                <MenuItem onClick={() => handleMarkAsPrimary(img)}>
                  Mark as primary
                </MenuItem>
              </Menu>
            </ThumbnailMenuWrapper>
          )}
        </ThumbnailItem>
      ))}
    </ThumbnailContainer>
  );
};
